import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import API_BASE from '../config';
import { creditAwareFetch } from '../hooks/creditAwareFetch';

// Minimal ISO 3166 list for the global search. Not exhaustive but covers
// every country we're likely to prospect into. Extend as needed.
const ALL_COUNTRIES = [
  'Afghanistan','Albania','Algeria','Andorra','Angola','Argentina','Armenia','Australia','Austria',
  'Azerbaijan','Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium','Belize','Benin',
  'Bhutan','Bolivia','Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria','Burkina Faso',
  'Burundi','Cambodia','Cameroon','Canada','Chad','Chile','China','Colombia','Costa Rica',
  "Cote d'Ivoire",'Croatia','Cuba','Cyprus','Czech Republic','Denmark','Dominican Republic',
  'Ecuador','Egypt','El Salvador','Estonia','Ethiopia','Fiji','Finland','France','Gabon','Gambia',
  'Georgia','Germany','Ghana','Greece','Guatemala','Guinea','Guyana','Haiti','Honduras','Hong Kong',
  'Hungary','Iceland','India','Indonesia','Iraq','Ireland','Israel','Italy','Jamaica','Japan',
  'Jordan','Kazakhstan','Kenya','Kuwait','Kyrgyzstan','Laos','Latvia','Lebanon','Lesotho','Liberia',
  'Libya','Liechtenstein','Lithuania','Luxembourg','Madagascar','Malawi','Malaysia','Maldives',
  'Mali','Malta','Mauritania','Mauritius','Mexico','Moldova','Monaco','Mongolia','Montenegro',
  'Morocco','Mozambique','Myanmar','Namibia','Nepal','Netherlands','New Zealand','Nicaragua',
  'Niger','Nigeria','North Macedonia','Norway','Oman','Pakistan','Panama','Papua New Guinea',
  'Paraguay','Peru','Philippines','Poland','Portugal','Qatar','Romania','Rwanda','Saudi Arabia',
  'Senegal','Serbia','Seychelles','Sierra Leone','Singapore','Slovakia','Slovenia','Somalia',
  'South Africa','South Korea','Spain','Sri Lanka','Sudan','Sweden','Switzerland','Taiwan',
  'Tajikistan','Tanzania','Thailand','Togo','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan',
  'Uganda','Ukraine','United Arab Emirates','United Kingdom','United States','Uruguay','Uzbekistan',
  'Venezuela','Vietnam','Yemen','Zambia','Zimbabwe'
];

// Regional shortlists — given a seed country, suggest neighbours and trade partners.
// Used as quick-add chips. Anything not on this map falls back to global search.
const REGIONAL_SHORTLIST = {
  'United Kingdom': ['Ireland','Netherlands','Germany','France','Belgium','Sweden','Denmark','Norway'],
  'Ireland': ['United Kingdom','Netherlands','Germany','France','Belgium'],
  'United States': ['Canada','Mexico','United Kingdom','Australia'],
  'Canada': ['United States','United Kingdom','Mexico'],
  'Nigeria': ['Ghana','Kenya','South Africa',"Cote d'Ivoire",'Senegal','Cameroon','Egypt','Rwanda'],
  'Ghana': ['Nigeria',"Cote d'Ivoire",'Senegal','Kenya','South Africa'],
  'Kenya': ['Nigeria','Tanzania','Uganda','Rwanda','South Africa','Ethiopia'],
  'South Africa': ['Nigeria','Kenya','Botswana','Namibia','Zimbabwe','Mauritius'],
  'Egypt': ['United Arab Emirates','Saudi Arabia','Morocco','Jordan','Tunisia'],
  'Germany': ['Netherlands','France','Switzerland','Austria','Belgium','Poland','Denmark'],
  'France': ['Belgium','Switzerland','Germany','Spain','Italy','Luxembourg'],
  'Netherlands': ['Belgium','Germany','Luxembourg','United Kingdom','France'],
  'Spain': ['Portugal','France','Italy','Andorra'],
  'Italy': ['Switzerland','France','Austria','Slovenia'],
  'India': ['Sri Lanka','Bangladesh','Nepal','Bhutan','United Arab Emirates','Singapore'],
  'United Arab Emirates': ['Saudi Arabia','Qatar','Bahrain','Oman','Kuwait','Egypt','India'],
  'Australia': ['New Zealand','Singapore','United States','United Kingdom'],
  'Singapore': ['Malaysia','Indonesia','Thailand','Vietnam','Philippines','Hong Kong','Australia'],
  'Brazil': ['Argentina','Uruguay','Paraguay','Chile','Colombia'],
  'Mexico': ['United States','Guatemala','Belize','Costa Rica','Colombia']
};

// Find the best matching country from a free-text location string (e.g. "London, UK")
function inferCountry(locationStr) {
  if (!locationStr) return null;
  const s = locationStr.toLowerCase();
  // Try direct match first
  for (const c of ALL_COUNTRIES) {
    if (s.includes(c.toLowerCase())) return c;
  }
  // Common alternates
  const alternates = { 'uk': 'United Kingdom', 'usa': 'United States', 'us': 'United States', 'uae': 'United Arab Emirates' };
  for (const [k, v] of Object.entries(alternates)) {
    if (s.includes(k)) return v;
  }
  return null;
}

export default function FindSimilar() {
  const { id: clientId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const seedFromUrl = searchParams.get('seed');

  const [companies, setCompanies] = useState([]);
  const [seedId, setSeedId] = useState(seedFromUrl ? parseInt(seedFromUrl, 10) : null);
  const [seed, setSeed] = useState(null);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState(new Set());

  // Location selection state
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationSearch, setLocationSearch] = useState('');

  // Load companies for the picker
  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API_BASE}/clients/${clientId}/companies`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data.companies || []);
        setCompanies(list);
        if (seedId) {
          const found = list.find(c => c.id === seedId);
          if (found) setSeed(found);
        }
      } catch (e) {
        toast.error('Could not load companies');
      }
    }
    load();
  }, [clientId, seedId]);

  // Default location selection when seed changes
  useEffect(() => {
    if (!seed) return;
    const inferred = inferCountry(seed.location);
    if (inferred) setSelectedLocations([inferred]);
    else setSelectedLocations([]);
  }, [seed]);

  const seedCountry = seed ? inferCountry(seed.location) : null;
  const DEFAULT_SHORTLIST = ['United Kingdom','Ireland','United States','Germany','France','Netherlands','Singapore','United Arab Emirates'];
  const shortlist = (seedCountry && REGIONAL_SHORTLIST[seedCountry]) ? REGIONAL_SHORTLIST[seedCountry] : DEFAULT_SHORTLIST;

  function toggleLocation(country) {
    setSelectedLocations(prev =>
      prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country]
    );
  }

  function removeLocation(country) {
    setSelectedLocations(prev => prev.filter(c => c !== country));
  }

  function clearLocations() {
    setSelectedLocations([]);
  }

  const searchMatches = locationSearch.trim().length >= 2
    ? ALL_COUNTRIES
        .filter(c => c.toLowerCase().includes(locationSearch.toLowerCase()))
        .filter(c => !selectedLocations.includes(c))
        .slice(0, 8)
    : [];

  async function runSearch() {
    if (!seedId) {
      toast.error('Pick a seed company first');
      return;
    }
    setSearching(true);
    setResults(null);
    setSelected(new Set());
    try {
      const res = await creditAwareFetch(`${API_BASE}/companies/${seedId}/find-similar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ num_results: 25, locations: selectedLocations })
      });
      if (!res) { setSearching(false); return; }
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setResults(data);
      setSeed(data.seed);
      if (!data.results || data.results.length === 0) {
        toast("No similar companies found that aren't already in your workspace", { icon: 'ℹ' });
      }
    } catch (e) {
      toast.error('Could not find similar: ' + e.message);
    }
    setSearching(false);
  }

  function toggleSelected(domain) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain); else next.add(domain);
      return next;
    });
  }

  function selectAll() {
    if (!results) return;
    if (selected.size === results.results.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(results.results.slice(0, 10).map(r => r.domain)));
    }
  }

  async function importSelected() {
    if (!selected.size) return;
    if (selected.size > 10) {
      toast.error('Max 10 per batch');
      return;
    }
    setImporting(true);
    const toastId = toast.loading(`Adding ${selected.size} ${selected.size === 1 ? 'company' : 'companies'}...`);
    try {
      const toImport = results.results.filter(r => selected.has(r.domain));
      const res = await creditAwareFetch(`${API_BASE}/clients/${clientId}/icp-company-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companies: toImport })
      });
      if (!res) { setImporting(false); return; }
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      const count = data.imported || (data.companies && data.companies.length) || selected.size;
      toast.success(`Imported ${count} ${count === 1 ? 'company' : 'companies'}`, { id: toastId });
      setResults(prev => ({
        ...prev,
        results: prev.results.map(r => selected.has(r.domain) ? { ...r, already_in_workspace: true } : r)
      }));
      setSelected(new Set());
    } catch (e) {
      toast.error('Import failed: ' + e.message, { id: toastId });
    }
    setImporting(false);
  }

  // ── Styles ─────────────────────────────────────────────────────────────────
  const chipStyle = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
    padding: '0.3rem 0.7rem', fontSize: '0.78rem', borderRadius: '20px',
    border: '0.5px solid ' + (active ? '#8B1A1A' : '#E8E4E0'),
    background: active ? '#FFF5F5' : '#FFF',
    color: active ? '#8B1A1A' : '#4A5568',
    cursor: 'pointer', fontWeight: active ? '500' : '400'
  });

  return (
    <div style={{ padding: '2rem', maxWidth: '1100px', margin: '0 auto' }}>
      <button onClick={() => navigate(`/clients/${clientId}/prospects`)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.85rem', cursor: 'pointer', marginBottom: '1rem' }}>← Back to prospects</button>

      <h1 style={{ fontSize: '1.5rem', fontWeight: '500', margin: '0 0 0.3rem', color: '#1A1A1A' }}>Find similar companies</h1>
      <p style={{ margin: '0 0 1.5rem', fontSize: '0.85rem', color: '#888' }}>
        Pick a company you want more of. We'll surface lookalikes from across the web that aren't already in your workspace.
      </p>

      {/* Seed picker */}
      <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem', marginBottom: '1rem' }}>
        <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: '600', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Seed company</label>
        <select
          value={seedId || ''}
          onChange={e => setSeedId(e.target.value ? parseInt(e.target.value, 10) : null)}
          style={{ width: '100%', padding: '0.6rem 0.8rem', fontSize: '0.9rem', border: '0.5px solid #E8E4E0', borderRadius: '6px', backgroundColor: '#fff' }}>
          <option value="">— pick a company —</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>{c.name}{c.website ? ' (' + c.website.replace(/^https?:\/\//, '').replace(/\/.*$/, '') + ')' : ''}</option>
          ))}
        </select>
      </div>

      {/* Locations panel */}
      <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
          <label style={{ fontSize: '0.72rem', fontWeight: '600', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Locations {selectedLocations.length === 0 && <span style={{ fontWeight: '400', color: '#888', textTransform: 'none', letterSpacing: 'normal' }}>· searching globally</span>}
          </label>
          {selectedLocations.length > 0 && (
            <button onClick={clearLocations} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.75rem', cursor: 'pointer' }}>
              Clear all (search globally)
            </button>
          )}
        </div>

        {/* Selected chips */}
        {selectedLocations.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' }}>
            {selectedLocations.map(c => (
              <span key={c} style={chipStyle(true)}>
                {c}
                <button onClick={() => removeLocation(c)} style={{ background: 'none', border: 'none', color: '#8B1A1A', fontSize: '0.95rem', cursor: 'pointer', padding: 0, marginLeft: '0.15rem', lineHeight: 1 }}>×</button>
              </span>
            ))}
          </div>
        )}

        {/* Quick-add shortlist */}
        {shortlist.length > 0 && shortlist.some(c => !selectedLocations.includes(c)) && (
          <div style={{ marginBottom: '0.75rem' }}>
            <p style={{ margin: '0 0 0.4rem', fontSize: '0.72rem', color: '#888', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              Suggested markets:
              <span title={seedCountry ? 'Markets commonly prospected alongside ' + seedCountry + ' \u2014 grouped by trade flows and business proximity, not pure geography.' : 'A default starter set of markets you commonly prospect into. Set the seed company\'s location to get tailored suggestions.'}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '14px', height: '14px', borderRadius: '50%', border: '0.5px solid #BBB', color: '#888', fontSize: '0.65rem', cursor: 'help', fontWeight: '500' }}>?</span>
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {shortlist.filter(c => !selectedLocations.includes(c)).map(c => (
                <button key={c} onClick={() => toggleLocation(c)} style={chipStyle(false)}>
                  + {c}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Global search */}
        <div>
          <input
            type="text"
            value={locationSearch}
            onChange={e => setLocationSearch(e.target.value)}
            placeholder="Search any country..."
            style={{ width: '100%', padding: '0.5rem 0.7rem', fontSize: '0.85rem', border: '0.5px solid #E8E4E0', borderRadius: '6px' }}
          />
          {searchMatches.length > 0 && (
            <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {searchMatches.map(c => (
                <button key={c} onClick={() => { toggleLocation(c); setLocationSearch(''); }} style={chipStyle(false)}>
                  + {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Search button */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button onClick={runSearch} disabled={!seedId || searching} style={{ fontSize: '0.9rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.7rem 1.5rem', cursor: (!seedId || searching) ? 'not-allowed' : 'pointer', opacity: (!seedId || searching) ? 0.6 : 1, fontWeight: '500' }}>
          {searching ? 'Searching...' : 'Find similar →'}
        </button>
      </div>

      {/* Results */}
      {results && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: '500', margin: 0, color: '#1A1A1A' }}>
              {results.results.length} similar to <span style={{ color: '#8B1A1A' }}>{seed?.name}</span>
              {results.locations_applied && results.locations_applied.length > 0 && (
                <span style={{ fontSize: '0.78rem', color: '#888', fontWeight: '400', marginLeft: '0.5rem' }}>
                  in {results.locations_applied.join(', ')}
                </span>
              )}
            </h2>
            {results.results.length > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={selectAll} style={{ fontSize: '0.78rem', color: '#4A5568', background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '6px', padding: '0.4rem 0.8rem', cursor: 'pointer' }}>
                  {selected.size === results.results.length ? 'Deselect all' : 'Select 10'}
                </button>
                <button onClick={importSelected} disabled={!selected.size || importing} style={{ fontSize: '0.78rem', color: '#fff', background: '#276749', border: 'none', borderRadius: '6px', padding: '0.4rem 0.8rem', cursor: (!selected.size || importing) ? 'not-allowed' : 'pointer', opacity: (!selected.size || importing) ? 0.6 : 1, fontWeight: '500' }}>
                  {importing ? 'Importing...' : `Add ${selected.size} to prospects`}
                </button>
              </div>
            )}
          </div>

          {results.results.length === 0 ? (
            <div style={{ background: '#FAFAF8', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '2rem', textAlign: 'center', color: '#888', fontSize: '0.88rem' }}>
              No new lookalikes found. Either Exa returned none, or all matches are already in your workspace.
            </div>
          ) : (
            <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
              {results.results.map((r, i) => (
                <div key={r.domain} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '1rem 1.25rem', borderTop: i > 0 ? '0.5px solid #F0EDE9' : 'none', opacity: r.already_in_workspace ? 0.5 : 1 }}>
                  <input
                    type="checkbox"
                    checked={selected.has(r.domain)}
                    onChange={() => toggleSelected(r.domain)}
                    disabled={r.already_in_workspace}
                    style={{ marginTop: '0.25rem', cursor: r.already_in_workspace ? 'not-allowed' : 'pointer' }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
                      <p style={{ margin: 0, fontSize: '0.92rem', fontWeight: '500', color: '#1A1A1A' }}>{r.name}</p>
                      <a href={r.website} target="_blank" rel="noreferrer" style={{ fontSize: '0.75rem', color: '#888', textDecoration: 'none' }}>{r.domain} ↗</a>
                      {r.already_in_workspace && <span style={{ fontSize: '0.7rem', color: '#276749', background: '#F0FFF4', padding: '0.1rem 0.5rem', borderRadius: '20px' }}>Imported</span>}
                    </div>
                    {r.description && (
                      <p style={{ margin: 0, fontSize: '0.82rem', color: '#4A5568', lineHeight: '1.5' }}>{r.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
